import * as preact from "preact";
import * as _ from "underscore";

import * as deviceDetect from "deviceDetect";
import * as practiceUtils from "practiceUtils";
import * as languageMap from "shared/languageMap";
import { cn } from "shared/utils";

interface Props {
  featuredLanguages?: string[];
  languageType: "learning" | "first";
  searchOnlyMode?: boolean;
  /** The function to call when the language is selected */
  setLanguage: (languageCode: string) => void;
}

interface State {
  /** The search text */
  search: string;
  /** The currently selected result */
  selectedSearchResultIndex: number;
}

/**
 * This view shows a list of languages with a search box at the top that you can select from.
 * The `featuredLanguages` will be shown at the top in the order they are provided.
 */
export default class LanguageSelectorView extends preact.Component<
  Props,
  State
> {
  constructor(props: Props) {
    super(props);
    this.state = {
      search: "",
      selectedSearchResultIndex: 0,
    };
  }

  render() {
    const { languageType, setLanguage, searchOnlyMode } = this.props;
    const { search } = this.state;
    const featuredLanguages = this.props.featuredLanguages ?? [];

    let languages = languageMap.list.map((language) => ({
      ...language,
      englishNormalized: practiceUtils.normalize(language.english),
      nameNormalized: practiceUtils.normalize(language.name),
    }));
    const languageMapWithNormalizedValues = _.indexBy(languages, "code");

    if (languageType === "learning") {
      languages = languages.filter((language) => !language.firstLanguageOnly);
    }

    languages = languages.sort((a, b) => {
      return a.english.localeCompare(b.english);
    });

    const displayItems = [
      ...featuredLanguages
        .map((code) => ({
          type: "language",
          language: languageMapWithNormalizedValues[code],
        }))
        .sort((a, b) => {
          return a.language.english.localeCompare(b.language.english);
        }),
      ...(search || featuredLanguages.length === 0
        ? []
        : [{ type: "separator" }]),
      ...languages
        .filter((language) => !featuredLanguages.includes(language.code))
        .map((language) => ({
          type: "language",
          language,
        })),
    ];

    const searchNormalized = search.trim().toLowerCase();

    const filteredDisplayItems =
      searchNormalized !== ""
        ? displayItems
            .filter(
              (item) =>
                item.type === "separator" ||
                (item.type === "language" &&
                  (item.language.englishNormalized.includes(searchNormalized) ||
                    item.language.nameNormalized.includes(searchNormalized)))
            )
            .sort((a, b) => {
              // Feature languages which start with the search term first
              let aScore =
                (a.language.english.toLowerCase().startsWith(searchNormalized)
                  ? 10
                  : 0) +
                (a.language.name.toLowerCase().startsWith(searchNormalized)
                  ? 1
                  : 0);
              let bScore =
                (b.language.english.toLowerCase().startsWith(searchNormalized)
                  ? 10
                  : 0) +
                (b.language.name.toLowerCase().startsWith(searchNormalized)
                  ? 1
                  : 0);
              return bScore - aScore;
            })
            .slice(0, 10)
        : displayItems;

    const minimumResultsHeight = 100;

    return (
      <div
        class={cn({
          languageSelectorView: true,
          searchOnlyMode,
          browseAndSearchMode: !searchOnlyMode,
        })}
      >
        <input
          ref={(input) => {
            if (input && !deviceDetect.isSmall()) {
              // Really don't like this setTimeout, but it's the only way I can get it to work
              setTimeout(() => {
                input.focus();
              }, 100);
            }
          }}
          type="text"
          placeholder="Search for a language"
          value={search}
          onInput={(event) =>
            this.setState({
              search: event.currentTarget.value,
              selectedSearchResultIndex: 0,
            })
          }
          autoFocus={!deviceDetect.isSmall()}
          class="languageSearchInput"
          onKeyDown={(event) => {
            // On Enter, if there's a single result, select it
            if (
              event.key === "Enter" &&
              this.state.search &&
              filteredDisplayItems.length > 0
            ) {
              setLanguage(
                filteredDisplayItems[this.state.selectedSearchResultIndex]
                  .language.code
              );
            }
            if (event.key === "ArrowDown") {
              this.setState({
                selectedSearchResultIndex: Math.min(
                  this.state.selectedSearchResultIndex + 1,
                  filteredDisplayItems.length - 1
                ),
              });
              event.preventDefault();
            }
            if (event.key === "ArrowUp") {
              this.setState({
                selectedSearchResultIndex: Math.max(
                  this.state.selectedSearchResultIndex - 1,
                  0
                ),
              });
              event.preventDefault();
            }
          }}
        />
        {!this.props.searchOnlyMode || this.state.search ? (
          <ul>
            {filteredDisplayItems.map((item, index) => {
              switch (item.type) {
                case "language": {
                  const displayString = `${item.language.english} (${item.language.name})`;

                  // Highlight the search text!
                  const highlightIndex = displayString
                    .toLowerCase()
                    .indexOf(search.toLowerCase());
                  const before = displayString.slice(0, highlightIndex);
                  const match = displayString.slice(
                    highlightIndex,
                    highlightIndex + search.length
                  );
                  const after = displayString.slice(
                    highlightIndex + search.length
                  );

                  return (
                    <li>
                      <a
                        href="#"
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          setLanguage(item.language.code);
                        }}
                        class={cn({
                          selected:
                            this.state.search &&
                            index === this.state.selectedSearchResultIndex,
                        })}
                      >
                        {before}
                        <strong>{match}</strong>
                        {after}
                      </a>
                    </li>
                  );
                }
                case "separator":
                  return <li>---</li>;
              }
            })}
          </ul>
        ) : (
          <div style={{ height: minimumResultsHeight }}></div>
        )}
      </div>
    );
  }
}
